import React, { CSSProperties, FC, SyntheticEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import $ from 'jquery';
import classNames from 'classnames';
import classes from './styles/index.module.scss';
import { isElementInScope } from '../../utils/DOM';
import { OverlayTooltip } from '../Tooltip';
import tooltipClasses from '../Tooltip/styles/index.module.scss';
import { _addInstance, _removeLastInstance, closeActiveTooltip, closeLastTooltipByName } from './api';
import OverlayPosition from '../../utils/renderProps/OverlayPosition';
import Button from '../Button';
import { EMPTY_FUNCTION } from './const';
import { getTooltipContainer } from './utils';
import { ButtonProps } from '../Button/Button';
import { OpenCloseInstance } from './types';
import { ThemeStyle } from '../../utils/const';
import { AngleDownSmall } from '../Icons/AngleDownSmall';

declare global {
	interface Window {
		__REACT__TOOLTIP: Array<OpenCloseInstance>;
	}
}

window.__REACT__TOOLTIP = [];

type TooltipBtnProps = Omit<ButtonProps, 'icon'> & {
	tooltip: React.ReactNode;
	className?: string;
	disabled?: boolean;
	dataTestId?: string;
	styledButton?: boolean;
	tooltipTheme?: ThemeStyle;
	tooltipPosition?: string;
	groupName?: string;
	tooltipAlign?: string;
	ariaLabel?: string;
	hasTooltipIcon?: boolean;
	autoClose?: boolean;
	stopPropagation?: boolean;
	currentColor?: boolean;
	onClose?: () => void;
	onOpen?: (instance: OpenCloseInstance) => void;
	tabIndex?: number;
	stopPropagationOverlayTooltip?: boolean;
	style?: CSSProperties;
	tooltipClassName?: string;
	icon?: JSX.Element;
};

export const TooltipBtn: FC<React.PropsWithChildren<TooltipBtnProps>> = (props) => {
	const {
		tooltip,
		className = '',
		disabled,
		dataTestId,
		styledButton,
		tooltipTheme = ThemeStyle.Light,
		tooltipPosition = 'bottom',
		groupName = '',
		tooltipAlign = 'start',
		ariaLabel = '',
		hasTooltipIcon = true,
		autoClose,
		stopPropagation,
		currentColor,
		onClose = EMPTY_FUNCTION,
		onOpen = EMPTY_FUNCTION,
		tabIndex,
		stopPropagationOverlayTooltip,
		verticalAlign,
		style,
		textAlign,
		tooltipClassName,
		icon,
		children,
	} = props;

	const [isOpen, setIsOpen] = useState(false);
	const componentRef = useRef<null | HTMLButtonElement>(null);
	const tooltipContainerRef = useRef<null | Element>(null);

	useEffect(() => {
		tooltipContainerRef.current = getTooltipContainer();
	}, []);

	useEffect(() => {
		return () => {
			isOpen && setIsOpen(false);
		};
	}, [isOpen]);

	const getCloseHandler = () => () => {
		_removeLastInstance();
		setIsOpen(false);
		onClose();
	};

	/*the ref below emulates the behavior of class component concerning 'this' instance*/
	const tooltipInstance = useRef<OpenCloseInstance>({
		open: function () {
			closeLastTooltipByName(groupName);
			_addInstance(this);
			setIsOpen(true);
			onOpen(this);
		},
		close: getCloseHandler(),
		groupName,
	});

	useLayoutEffect(() => {
		tooltipInstance.current.close = getCloseHandler();
	});

	const handleComponentClick = (e: SyntheticEvent) => {
		if (stopPropagation) {
			e.stopPropagation();
		}

		if (disabled) {
			return;
		}

		if (isOpen) {
			tooltipInstance.current.close();
		} else {
			tooltipInstance.current.open();
		}
	};

	const position = `${tooltipPosition}_${tooltipAlign}`;
	const unStyledButtonClassNames = classNames(className, classes.tooltipBtn, 'tooltipButton', {
		[classes.tooltipBtn_disabled]: disabled,
		[classes.tooltipBtn_currentColor]: currentColor,
		[classes.tooltipBtn_verticalAlignTop]: verticalAlign === 'top',
		[classes.tooltipBtn_verticalAlignMiddle]: verticalAlign === 'middle',
		[classes.tooltipBtn_verticalAlignBottom]: verticalAlign === 'bottom',
		[classes.tooltipBtn_textAlignLeft]: textAlign === 'left',
		[classes.tooltipBtn_textAlignRight]: textAlign === 'right',
	});

	const styledButtonClassNames = classNames(className, 'tooltipButton', {
		[classes.tooltipBtn_currentColor]: currentColor,
	});

	return (
		<>
			{styledButton && (
				// @ts-ignore
				<Button
					{...props}
					className={styledButtonClassNames}
					onClick={(e) => handleComponentClick(e)}
					ref={componentRef}
					active={isOpen}
					ariaLabel={ariaLabel}
					dataTestId={dataTestId}
					startIcon={icon}
					endIcon={hasTooltipIcon ? <AngleDownSmall /> : undefined}
				>
					{children}
				</Button>
			)}
			{!styledButton && (
				<button
					type='button'
					className={unStyledButtonClassNames}
					onClick={handleComponentClick}
					ref={componentRef}
					aria-label={ariaLabel}
					style={style}
					data-testid={dataTestId}
					tabIndex={tabIndex}
					disabled={disabled}
				>
					{children}
				</button>
			)}
			{isOpen && (
				<OverlayPosition
					position={position}
					possiblePositions={['bottom_start', 'bottom_end', 'top_start', 'top_end']}
					callerComponentRef={componentRef}
					render={() => {
						return (
							<OverlayTooltip
								theme={tooltipTheme}
								autoClose={autoClose}
								stopPropagation={stopPropagationOverlayTooltip}
								className={tooltipClassName}
							>
								{tooltip}
							</OverlayTooltip>
						);
					}}
				/>
			)}
		</>
	);
};

document.body.addEventListener('click', (e) => {
	const $target = $(e.target);

	const isTooltip = isElementInScope([`.${tooltipClasses.tooltip}`, '.tooltipButton'], $target);

	if (isTooltip) {
		return;
	}

	closeActiveTooltip();
});
